import { useEffect, useRef } from 'react';
import { showDialog } from '../components/confirm/dialogBus';

/**
 * Global "unsaved changes" guard — the ONE mechanism that stops a user losing
 * edits they have typed but not saved, on EVERY form (old, new, and future).
 *
 * Matches the legacy ALPACA prompt: "Unsaved changes will be lost. Do you want
 * to proceed?" (OK / Cancel).
 *
 * Why a module-level registry instead of React Router's `useBlocker`: the app
 * mounts a plain `<BrowserRouter>` (declarative), and `useBlocker` throws
 * ("must be used within a data router") unless a data router is used. A Set of
 * getters (not a single slot) is used because two guarded forms can be mounted
 * at once — the SDMS Result Entry sheet opens as a popup OVER the QC Batch form,
 * and either may hold unsaved work.
 *
 * TWO layers, so a form is protected whether or not its author remembered:
 *
 *   1. AUTOMATIC (nothing to wire) — `installUnsavedTracking()` (mounted once in
 *      AppShell) watches real user edits to form controls on the page. A page
 *      that offers a way to commit or discard (Save, Save Draft, Submit,
 *      Validate, Approve, Reject, Release, Update, Finish, Complete, Cancel —
 *      the caption varies, and a disabled button counts) and whose controls the
 *      user has changed is "dirty" until it is saved, discarded, or unmounted.
 *      Custom widgets with no native input (dropdowns, switches) announce their
 *      change with `markUserEdit`. Search/filter boxes, grid row-tick boxes and
 *      read-only controls are ignored.
 *
 *   2. EXPLICIT (precise) — a form that knows its own dirty state calls
 *      `useUnsavedGuard(isDirty)` (or `registerUnsavedGuard`). Use this for
 *      custom widgets the automatic layer cannot see (grid cell editors,
 *      multi-selects, in-memory sheets) — the two layers are OR-ed.
 *
 * Every exit is covered:
 *   - Sidebar / rail / cross-page navigation — AppShell.open() awaits
 *     confirmDiscardIfUnsaved() before moving.
 *   - Sign Out — the user-initiated button awaits it too (an idle-timeout
 *     auto sign-out is deliberately NOT guarded: nobody is there to answer).
 *   - In-page exits (Cancel / Back / Close / breadcrumb / List View tab /
 *     prev-next arrows) — intercepted globally, so no form has to wrap them.
 *   - Browser tab close / refresh — the `beforeunload` prompt.
 *
 * A form escapes the automatic layer for a control with `data-unsaved-ignore`
 * on the control or any ancestor.
 */
const guards = new Set<() => boolean>();
/** Optional per-form "the user chose to leave" cleanup, keyed by its getter. */
const leaveHandlers = new Map<() => boolean, () => void | Promise<void>>();

/**
 * Register a form's dirty-state getter; returns its unregister function.
 * `onLeave` runs only when the user answers OK to "Unsaved changes will be lost"
 * while THIS form reports dirty — use it to remove a draft the form had to
 * persist early (e.g. a new Job ID created to enter the Samples step), so
 * "lost" really means lost, as in legacy ALPACA's uncommitted ObjectSpace.
 */
export function registerUnsavedGuard(
  getter: () => boolean,
  onLeave?: () => void | Promise<void>,
): () => void {
  guards.add(getter);
  if (onLeave) leaveHandlers.set(getter, onLeave);
  return () => { guards.delete(getter); leaveHandlers.delete(getter); };
}

// ───────────────────────── automatic edit tracking ─────────────────────────

/** Controls the user has actually edited (real input/change events). */
const touched = new Set<HTMLElement>();
/** A control's value the first time the user focused it — dirty means "differs
 *  from this", so typing something and typing it back is NOT a change. */
const baselines = new WeakMap<HTMLElement, string>();

/** Widgets that have no native input to watch (custom dropdowns, switches, chips)
 *  announce a user change through `markUserEdit`; the element is remembered here
 *  and counts as unsaved for as long as it stays on the page. */
const customEdits = new Set<HTMLElement>();

const CONTROL_SEL = 'input,textarea,select,[contenteditable]:not([contenteditable="false"])';
const SKIP_INPUT_TYPES = new Set(['hidden', 'button', 'submit', 'reset', 'image', 'search']);
/** Search / filter / paging boxes are not record edits. */
const NON_EDIT_HINT = /search|filter|find/i;
/** Where an EDIT can live: the routed page, or a dialog opened over it. The
 *  shared confirm/alert dialogs (role=alertdialog) are never a form. */
const EDIT_ROOT_SEL = '.content,[role="dialog"]';
const IGNORE_SEL = '[data-unsaved-ignore],[role="alertdialog"],.cfd-overlay,'
  + '.adg thead,.adg-footer,.adg-cb-col,.cb-col,nav';

/** Shown on screen? `checkVisibility()` where the browser has it (layout-aware);
 *  otherwise walk up looking for `display:none` (jsdom / older engines, which
 *  have no layout to ask). A Save button parked in a hidden tab does not count. */
const isElVisible = (el: HTMLElement): boolean => {
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    if (getComputedStyle(n).display === 'none') return false;
  }
  return true;
};

const readValue = (el: HTMLElement): string => {
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
    return el.checked ? '1' : '0';
  }
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value;
  }
  return el.textContent ?? '';   // contenteditable
};

/** Is this control a place the user enters record data (as opposed to a
 *  search box, grid filter, pager, or row-select tick)? */
function isRecordControl(el: HTMLElement): boolean {
  if (!el.isConnected || el.closest(IGNORE_SEL)) return false;
  if (!el.closest(EDIT_ROOT_SEL)) return false;
  if (el instanceof HTMLInputElement) {
    if (SKIP_INPUT_TYPES.has(el.type) || el.readOnly || el.disabled) return false;
    // A grid's row-select tick: labelled "Select row" / "Select all rows".
    if ((el.type === 'checkbox' || el.type === 'radio') && /^select\b/i.test(el.getAttribute('aria-label') ?? '')) return false;
  } else if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    if (el.disabled || (el instanceof HTMLTextAreaElement && el.readOnly)) return false;
  }
  const hint = `${el.getAttribute('placeholder') ?? ''} ${el.getAttribute('aria-label') ?? ''} `
    + `${el.getAttribute('name') ?? ''} ${el.id} ${el.getAttribute('title') ?? ''} ${el.getAttribute('role') ?? ''}`;
  return !NON_EDIT_HINT.test(hint);
}

/** A caption that COMMITS or DISCARDS the record: Save / Save Draft / Save & Close,
 *  Submit, Validate, Approve, Reject, Release, Update, Finish, Complete — or a
 *  Cancel / Discard button (every wizard and editor offers one). */
const COMMIT_CAPTION = /\b(save|submit|validate|approve|reject|release|update|finish|complete)\b/i;
const DISCARD_CAPTION = /^(cancel|discard)$/i;
const stripGlyphs = (s: string) => s.replace(/^[^A-Za-z]+/, '').trim();

/** A record being edited offers a way to commit or discard it. That is what
 *  separates a form from a list whose query/filter boxes are being used — and
 *  the forms differ only in the CAPTION (Save, Save Draft, Submit, Validate,
 *  Approve, …), so any commit/discard verb counts. A DISABLED button counts too:
 *  a form's Save usually starts disabled until it is valid, and that must not
 *  hide the very first keystroke. A stepper pill that merely LABELS a step
 *  ("Save", "Submit" in the workflow island) does not count. */
function hasFormAction(scope: ParentNode): boolean {
  for (const b of Array.from(scope.querySelectorAll<HTMLElement>('button'))) {
    if (b.closest('.si-step,.step-island,[role="alertdialog"],nav')) continue;
    const label = stripGlyphs(b.textContent ?? '');
    if (!label || !(COMMIT_CAPTION.test(label) || DISCARD_CAPTION.test(label))) continue;
    if (isElVisible(b)) return true;
  }
  return false;
}

/** The form root a control lived in when the user edited it. */
const rootOf = new WeakMap<HTMLElement, HTMLElement>();

/**
 * A page that RE-CREATES its inputs on every render (a component declared inside
 * another component — App Config does this) swaps the element the user typed
 * into for an identical new one, and the old element then looks "gone", i.e. the
 * form looks closed. Find the successor: same form root, same kind of control,
 * holding the very value the user left in the old one. Without this the edit
 * would be forgotten and leaving would silently discard it.
 */
function findReplacement(old: HTMLElement): HTMLElement | null {
  const root = rootOf.get(old);
  if (!root?.isConnected) return null;             // the whole form is gone
  const typed = readValue(old);
  const base = baselines.get(old);
  if (base !== undefined && typed === base) return null;   // it was never changed
  for (const c of Array.from(root.querySelectorAll<HTMLElement>(CONTROL_SEL))) {
    if (c === old || touched.has(c) || c.tagName !== old.tagName) continue;
    if (c instanceof HTMLInputElement && old instanceof HTMLInputElement && c.type !== old.type) continue;
    if (isRecordControl(c) && readValue(c) === typed) return c;
  }
  return null;
}

function autoDirty(): boolean {
  for (const el of Array.from(customEdits)) {
    if (!el.isConnected) { customEdits.delete(el); continue; }
    if (el.closest(IGNORE_SEL)) continue;
    return true;                              // a custom widget the user changed, still on screen
  }
  for (const el of Array.from(touched)) {
    let cur: HTMLElement = el;
    if (!el.isConnected) {
      touched.delete(el);
      const rep = findReplacement(el);
      if (!rep) continue;                     // form closed → nothing to lose
      baselines.set(rep, baselines.get(el) ?? '');
      const root = rootOf.get(el);
      if (root) rootOf.set(rep, root);
      touched.add(rep);                       // the same edit, on the re-created control
      cur = rep;
    }
    if (!isRecordControl(cur)) continue;
    const base = baselines.get(cur);
    if (base === undefined || readValue(cur) !== base) return true;
  }
  return false;
}

/**
 * Tell the guard the USER just changed something a custom widget shows — a
 * dropdown pick, a switch, a chip, a picker result. Native inputs need no call
 * (the tracker sees their events); custom widgets have no input to watch, so the
 * shared ones (LookupCombo, AiCombo, CheckedCombo, ComboSelect, …) call this
 * from their user-selection handler, passing their own root/trigger element.
 * Call it ONLY for a user action, never when a value arrives programmatically.
 */
export function markUserEdit(from?: Element | null): void {
  if (!from) return;
  from.dispatchEvent(new CustomEvent('ai:user-edit', { bubbles: true }));
}

/** Forget what has been touched and treat every control's CURRENT value as the
 *  clean baseline. Called after a successful save/delete, and after the user
 *  agrees to discard, so a form that stays mounted (Save-and-stay, prev/next
 *  record) starts clean. */
export function resetUnsavedTracking(): void {
  for (const el of Array.from(touched)) {
    if (el.isConnected) baselines.set(el, readValue(el));
  }
  touched.clear();
  customEdits.clear();
}

// ───────────────────────────── public helpers ──────────────────────────────

/** True when ANY registered form — or the automatic tracker — reports unsaved changes. */
export function hasUnsavedChanges(): boolean {
  for (const g of guards) {
    try {
      if (g()) return true;
    } catch {
      /* a throwing getter never blocks navigation */
    }
  }
  return autoDirty();
}

let pendingPrompt: Promise<boolean> | null = null;
/** Set for an instant after the user answered OK, so a form's own guarded
 *  handler (which runs when the intercepted click is replayed) does not ask a
 *  second time. */
let approved = false;

/**
 * If a form has unsaved changes, ask the user to confirm leaving.
 * Returns true when it is OK to proceed (nothing unsaved, or the user chose to
 * leave); false to stay. Safe to call from any context (uses the global
 * showDialog, not a hook). Concurrent callers share ONE dialog.
 */
export async function confirmDiscardIfUnsaved(): Promise<boolean> {
  if (approved || !hasUnsavedChanges()) return true;
  if (pendingPrompt) return pendingPrompt;
  pendingPrompt = (async () => {
    try {
      const leave = (await showDialog({
        type: 'confirm',
        title: 'Unsaved Changes',
        message: 'Unsaved changes will be lost. Do you want to proceed?',
        confirmLabel: 'OK',
        cancelLabel: 'Cancel',
      })) === true;
      if (leave) {
        // Run each dirty form's leave cleanup BEFORE the tracker is reset (the
        // getters still report dirty here). A failing cleanup never blocks leaving.
        for (const [getter, onLeave] of [...leaveHandlers]) {
          try { if (getter()) await onLeave(); } catch { /* non-fatal */ }
        }
        resetUnsavedTracking();
        approved = true;
        setTimeout(() => { approved = false; }, 60);
      }
      return leave;
    } finally {
      pendingPrompt = null;
    }
  })();
  return pendingPrompt;
}

/** Run `action` only if there is nothing unsaved, or the user agrees to lose it. */
export async function guardedExit(action: () => void): Promise<void> {
  if (await confirmDiscardIfUnsaved()) action();
}

/**
 * The same ONE prompt, scoped to a POPUP: asks only when a control INSIDE `scope`
 * holds an edit, and on OK forgets only those edits — so closing a popup (Esc /
 * ✕ / backdrop) never asks about, nor silently clears, unsaved work on the page
 * behind it. Same test the global click guard applies to a popup's own Cancel /
 * Close (dirtyInsideDialog). `dirty` lets a popup that knows its own state say so —
 * the automatic tracker only watches a popup with a Save-style caption, so a wizard
 * whose buttons read OK / Previous / Next would otherwise never count as edited.
 * First used by the Reagent Prep Notepad (2026-11-03).
 */
export async function confirmDiscardIfUnsavedIn(scope: Element | null, dirty = false): Promise<boolean> {
  if (approved || !(dirty || (scope && dirtyInsideDialog(scope)))) return true;
  const leave = (await showDialog({
    type: 'confirm',
    title: 'Unsaved Changes',
    message: 'Unsaved changes will be lost. Do you want to proceed?',
    confirmLabel: 'OK',
    cancelLabel: 'Cancel',
  })) === true;
  if (leave && scope) {
    for (const el of [...customEdits]) if (scope.contains(el)) customEdits.delete(el);
    for (const el of [...touched]) if (scope.contains(el)) touched.delete(el);
  }
  if (leave) {
    approved = true;
    setTimeout(() => { approved = false; }, 60);
  }
  return leave;
}

/**
 * Registers `isDirty` with the global guard. Call once per form; the getter
 * reads the latest value via a ref so the registration itself never needs to
 * change. (The browser `beforeunload` prompt is installed once, globally, by
 * `installUnsavedTracking` — it covers registered forms too.)
 */
export function useUnsavedGuard(isDirty: boolean): void {
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  useEffect(() => registerUnsavedGuard(() => dirtyRef.current), []);
}

// ───────────────────────── global listeners (mounted once) ─────────────────

/** Buttons whose whole job is to LEAVE the current form. */
const EXIT_LABEL = /^(cancel|back|close|discard|list view|detail view|home)$/i;
/** Non-button exits: the shared toggle, prev/next arrows and breadcrumbs. */
const EXIT_SEL = '.view-toggle button,.sd-na,.sd-bc a,.dp-breadcrumb span:not(.dp-bc-active)';
const DIALOG_SEL = '[role="dialog"],.modal,.ref-dialog';

function exitTarget(t: EventTarget | null): HTMLElement | null {
  const el = t instanceof Element ? t : null;
  if (!el || !el.closest(EDIT_ROOT_SEL) || el.closest('[role="alertdialog"],.cfd-overlay,nav')) return null;
  const hit = el.closest<HTMLElement>(`${EXIT_SEL},button`);
  if (!hit) return null;
  if (hit.matches(EXIT_SEL)) return hit;
  // Strip the leading icon/arrow glyphs ("↩︎ Cancel", "← Back") before matching.
  const label = (hit.textContent ?? '').replace(/^[^A-Za-z]+/, '').trim();
  return EXIT_LABEL.test(label) ? hit : null;
}

/** Only the dialog a Cancel/Close belongs to should be asked about — closing a
 *  small picker over a dirty form is not "leaving the form". */
function dirtyInsideDialog(dlg: Element): boolean {
  for (const el of customEdits) {
    if (el.isConnected && dlg.contains(el)) return true;
  }
  for (const el of touched) {
    if (el.isConnected && dlg.contains(el) && isRecordControl(el)
        && (baselines.get(el) === undefined || readValue(el) !== baselines.get(el))) return true;
  }
  return false;
}

let installCount = 0;
let uninstall: (() => void) | null = null;

/**
 * Installs the automatic edit tracking + the in-page exit interception + the
 * browser-close prompt. Mounted ONCE by AppShell; ref-counted so a StrictMode
 * double-mount is harmless. `acceptSyntheticEvents` is for unit tests only
 * (jsdom's dispatched events are never `isTrusted`).
 */
export function installUnsavedTracking(opts?: { acceptSyntheticEvents?: boolean }): () => void {
  installCount++;
  if (installCount === 1) {
    const trusted = (e: Event) => e.isTrusted || !!opts?.acceptSyntheticEvents;
    const controlOf = (e: Event) => (e.target instanceof Element ? e.target.closest<HTMLElement>(CONTROL_SEL) : null);

    const onFocusIn = (e: FocusEvent) => {
      const el = controlOf(e);
      if (el && !baselines.has(el)) baselines.set(el, readValue(el));
    };
    const onEdit = (e: Event) => {
      if (!trusted(e)) return;               // scripted fills are not user edits
      const el = controlOf(e);
      if (!el || !isRecordControl(el)) return;
      const root = el.closest<HTMLElement>(EDIT_ROOT_SEL);
      if (!root || !hasFormAction(root)) return;   // nothing to commit/discard → a filter, not a form
      if (!baselines.has(el)) baselines.set(el, '');
      rootOf.set(el, root);
      touched.add(el);
    };

    /** A custom widget announced a user change (see `markUserEdit`). */
    const onUserEdit = (e: Event) => {
      const from = e.target instanceof HTMLElement ? e.target : null;
      if (!from || from.closest(IGNORE_SEL)) return;
      const root = from.closest<HTMLElement>(EDIT_ROOT_SEL);
      if (!root || !hasFormAction(root)) return;
      customEdits.add(from);
    };

    /** Real toggle controls that are not native inputs — a switch, a custom
     *  checkbox/radio, a pressed/checked button. Tabs, the List/Detail toggle and
     *  anything inside a filter widget are navigation, not edits. */
    const TOGGLE_SEL = '[role="switch"],[role="checkbox"],[role="radio"],[role="menuitemcheckbox"],'
      + '[role="menuitemradio"],[aria-checked],[aria-pressed]';
    const onToggleClick = (e: MouseEvent) => {
      if (!trusted(e) || !(e.target instanceof Element)) return;
      const t = e.target.closest<HTMLElement>(TOGGLE_SEL);
      if (!t || t instanceof HTMLInputElement || t.closest(IGNORE_SEL + ',[role="tablist"],[role="tab"],.view-toggle')) return;
      if (t.hasAttribute('disabled') || t.getAttribute('aria-disabled') === 'true') return;
      const root = t.closest<HTMLElement>(EDIT_ROOT_SEL);
      if (!root || !hasFormAction(root)) return;
      customEdits.add(t);
    };

    let replaying = false;
    const onClick = (e: MouseEvent) => {
      if (replaying || !trusted(e) || approved) return;
      const hit = exitTarget(e.target);
      if (!hit) return;
      // A Cancel/Close INSIDE a popup asks only about that popup. A popup is recognised either by
      // being portaled outside `.content` (the shared <AppDialog>) OR by declaring itself modal
      // (`aria-modal="true"`) — a bespoke picker rendered INLINE under `.content` (QC Batch
      // Instruments/Reagents Scrim) is a popup too, and used to fall through to the whole-form
      // check, so closing an untouched picker over a dirty form falsely prompted (user report
      // 2026-09-22). A full-page detail view never sets aria-modal, so its Cancel still checks
      // the whole form.
      const dlg = hit.closest(DIALOG_SEL);
      const isPopup = !!dlg && (dlg.getAttribute('aria-modal') === 'true' || !hit.closest('.content'));
      const dirty = isPopup ? dirtyInsideDialog(dlg!) : hasUnsavedChanges();
      if (!dirty) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      void confirmDiscardIfUnsaved().then((ok) => {
        if (!ok) return;
        replaying = true;
        try { hit.click(); } finally { replaying = false; }
      });
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return;
      e.preventDefault();
      // Legacy browsers required a returnValue; modern ones show a generic prompt.
      e.returnValue = '';
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('input', onEdit, true);
    document.addEventListener('change', onEdit, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('click', onToggleClick, true);
    document.addEventListener('ai:user-edit', onUserEdit);
    window.addEventListener('beforeunload', onBeforeUnload);
    uninstall = () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('input', onEdit, true);
      document.removeEventListener('change', onEdit, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('click', onToggleClick, true);
      document.removeEventListener('ai:user-edit', onUserEdit);
      window.removeEventListener('beforeunload', onBeforeUnload);
      touched.clear();
      customEdits.clear();
    };
  }
  return () => {
    installCount = Math.max(0, installCount - 1);
    if (installCount === 0) { uninstall?.(); uninstall = null; }
  };
}
